import type { CSSProperties } from 'react';

/* ------------------------------------------------------------------ */
/*  Premise — Clay palette (ported verbatim from the prototype).       */
/*  Status colours are FIXED: paid green / due amber / overdue red.    */
/* ------------------------------------------------------------------ */
export const C = {
  accent: '#b0532e',
  accent2: '#8a3f21',
  soft: '#f4e5db',
  hi: '#2f6b57', // money-highlight pine
  bg: '#f0efe9', // cool-grey paper
  card: '#ffffff',
  ink: '#241d18',
  ink2: '#4f463f',
  muted: '#8a7a6f',
  line: '#e6e2da',
  line2: '#efece5',
  side: '#2a1c14',
  red: '#b0472e',
  redSoft: '#f6e5df',
  green: '#1c7a5f',
  greenSoft: '#e2efe9',
  amber: '#9a6f24',
  amberSoft: '#f4ecd9',
} as const;

export const SERIF =
  'ui-serif, "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif';
export const MONO =
  'ui-monospace, "SF Mono", "Cascadia Code", Menlo, Consolas, monospace';
export const SANS = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

/** Tabular monospace style helper for numbers. */
export const mono = (s: CSSProperties = {}): CSSProperties => ({
  fontFamily: MONO,
  fontVariantNumeric: 'tabular-nums',
  ...s,
});

/* ------------------------------------------------------------------ */
/*  Semantic roles.                                                    */
/*                                                                     */
/*  C above is the raw Clay palette and does not move. These name what */
/*  a colour MEANS, so one meaning cannot drift into several hexes —    */
/*  which had already happened: "needs attention" existed as #7a5518,  */
/*  #946512 and C.amber, and "good" as both C.green and C.hi.          */
/*  New code reaches for S; existing C usage stays valid.              */
/* ------------------------------------------------------------------ */
export const S = {
  surface: { page: C.bg, card: C.card, sunk: C.line2, inverse: C.side, accent: C.soft },
  text: { primary: C.ink, secondary: C.ink2, muted: C.muted, accent: C.accent2, onAccent: '#ffffff' },
  edge: { hairline: C.line, divider: C.line2 },
  /** Status colours are FIXED by design: paid green / due amber / overdue red. */
  status: {
    paid: { fg: C.green, bg: C.greenSoft },
    due: { fg: '#7a5518', bg: C.amberSoft }, // darkest of the three ambers: best contrast on amberSoft
    overdue: { fg: C.red, bg: C.redSoft },
    neutral: { fg: C.muted, bg: C.line2 },
    example: { fg: C.accent2, bg: C.soft },
  },
  /** Money is pine, deliberately NOT the same green as paid status. */
  money: { positive: C.hi, negative: C.red },
} as const;

/** Corner radii. Four steps replace the twelve the app grew by hand. */
export const R = { sm: 8, md: 10, lg: 14, pill: 999 } as const;

/** Type scale by role. Replaces 43 hand-picked sizes; each step is one the app already used most. */
export const T = {
  label: 11,
  small: 12.5,
  body: 13.5,
  lead: 15,
  head: 18,
  title: 23,
  figure: 33,
} as const;
